/**
 * Popup manager class
 *
 * @since 1.0.0
 */

import { createElement } from 'react';
import { createRoot } from 'react-dom/client';

import type { RedFoxCartData, RedFoxCartProduct } from '../../types/cart';
import { Popup } from '../popup/component';
import type { PopupManagerRenderOptions } from './types';

/**
 * Popup manager class
 */
class PopupManager {
  private container: HTMLElement | null = null;
  private root: ReturnType<typeof createRoot> | null = null;
  private isVisible: boolean = false;

  /**
   * Initialize popup manager
   */
  init() {
    this.createContainer();
  }

  /**
   * Create popup container
   */
  private createContainer() {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'redfox-added-to-cart-popup-root';
      document.body.appendChild(this.container);
      this.root = createRoot(this.container);
    }
  }

  /**
   * Show popup
   */
  show(title?: string, content?: string, className?: string) {
    if (!this.container || !this.root) {
      this.init();
    }

    this.isVisible = true;
    this.render({ title, content, className, type: 'default' });
  }

  /**
   * Show cart popup
   */
  showCart(addedProduct: RedFoxCartProduct, cartData?: RedFoxCartData, className?: string) {
    if (!this.container || !this.root) {
      this.init();
    }

    this.isVisible = true;
    this.render({
      addedProduct,
      cartData,
      className,
      type: 'cart',
    });
  }

  /**
   * Hide popup
   */
  hide() {
    this.isVisible = false;
    this.render({});
  }

  /**
   * Render popup
   */
  private render(options: PopupManagerRenderOptions) {
    if (this.root) {
      this.root.render(
        createElement(Popup, {
          isVisible: this.isVisible,
          onClose: () => this.hide(),
          title: options.title,
          content: options.content,
          addedProduct: options.addedProduct,
          cartData: options.cartData,
          className: options.className,
          type: options.type,
        })
      );
    }
  }

  /**
   * Destroy popup
   */
  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.container) {
      document.body.removeChild(this.container);
      this.container = null;
    }
    this.isVisible = false;
  }
}

// Create global popup manager instance
export const popupManager = new PopupManager();
